import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useMaps } from '/@/hooks/maps';
import { getSessionCountry } from '/@/store/session/selectors';
import type { Field } from '/@/types/field';

/** Risk levels a map value can carry, worst first — the order the design lists them in. */
const RISK_ORDER = ['high', 'medium', 'low'] as const;
export type Risk = (typeof RISK_ORDER)[number];

/** A disease the field is exposed to, resolved to the defensive-profile metric that scores it. */
export type FieldRisk = {
    /** `CornMetric` id — matches `product.metrics[].id`. */
    cornMetricId: number;
    name: string;
    risk: Risk;
    order: number;
};

type ApiMap = {
    id: number;
    name?: Record<string, string> | string;
    cornMetric?: { id: number; name: string; order?: number } | null;
};

const isRelevant = (risk: string | null): risk is Risk => risk === 'high' || risk === 'medium';

/**
 * Diseases with a High or Medium incidence on this field, joined to their defensive-profile
 * metric.
 *
 * The field's `mapValues` carry the risk level and the id of the map they came from; the map
 * in turn points at the `CornMetric` that scores hybrids for that disease. Maps without a
 * `cornMetric` (soil moisture, weeds, …) drop out of the join, as do Low-risk values.
 */
export const useFieldRisks = (field?: Field): { risks: FieldRisk[]; isLoading: boolean } => {
    const country = useSelector(getSessionCountry);
    const { data: maps, isValidating } = useMaps({ country: country?.code });

    const risks = useMemo(() => {
        if (!field?.mapValues?.length || !maps) return [];

        const mapsById = new Map<number, ApiMap>((maps as ApiMap[]).map((map) => [map.id, map]));

        const byMetric = new Map<number, FieldRisk>();
        field.mapValues.forEach((mapValue) => {
            if (!isRelevant(mapValue.risk)) return;
            const cornMetric = mapsById.get(mapValue.map)?.cornMetric;
            if (!cornMetric) return;

            // A field can carry several values for the same disease (different map versions);
            // keep the worst one.
            const existing = byMetric.get(cornMetric.id);
            if (existing && RISK_ORDER.indexOf(existing.risk) <= RISK_ORDER.indexOf(mapValue.risk)) return;

            byMetric.set(cornMetric.id, {
                cornMetricId: cornMetric.id,
                name: cornMetric.name,
                risk: mapValue.risk,
                order: cornMetric.order ?? 0,
            });
        });

        return [...byMetric.values()].sort(
            (a, b) => RISK_ORDER.indexOf(a.risk) - RISK_ORDER.indexOf(b.risk) || a.order - b.order
        );
    }, [field?.mapValues, maps]);

    return { risks, isLoading: isValidating && !maps };
};
